/** Seeded upper-atmosphere curtains, attached to the rotating planet. Actual
 * geometry keeps mountains in front; transparent decks draw over it. Emission
 * adds together without requiring triangle sorting. One batch per depth slice. */
import * as THREE from 'three';
import { auroraAt, auroraConfig, auroraDarkness, rngFromString, type AuroraState, type PlanetDef } from '@hyperlight/shared';

const VERT = /* glsl */`
  #include <common>
  #include <logdepthbuf_pars_vertex>
  attribute vec3 curtain;
  attribute float facet;
  uniform mat3 uBasis;
  uniform float uRadius, uHeight, uOval, uPhase, uDetailPhase;
  varying vec3 vLocal;
  varying vec2 vSheet;
  varying float vFacet, vSeed;
  void main() {
    float a=position.x, h=position.y, band=curtain.x, hemi=curtain.y, seed=curtain.z;
    // Connected folds: all levels share the same footpoint. The fine rays are
    // stretched upward along the field, not animated as disconnected particles.
    float longitude=a+.065*sin(a*3.0+seed+uPhase)+.028*sin(a*11.0-seed-uPhase*2.0);
    float theta=uOval+(band-1.0)*.12
      +.075*sin(a*3.0+seed+uPhase)+.035*sin(a*7.0-seed-uPhase)
      +.014*sin(a*19.0+seed+uDetailPhase)+h*.025*sin(a*4.0+seed+uPhase);
    vec3 dir=uBasis*vec3(sin(theta)*cos(longitude),hemi*cos(theta),sin(theta)*sin(longitude));
    float lower=3.40+band*.13+.18*sin(a*6.0+seed+uPhase);
    float tall=2.65+.60*sin(a*5.0-seed+uPhase)+.30*sin(a*13.0+seed);
    vec3 p=dir*(uRadius+uHeight*(lower+h*tall));
    vLocal=p; vSheet=vec2(a,h); vFacet=facet; vSeed=seed;
    gl_Position=projectionMatrix*modelViewMatrix*vec4(p,1.0);
    #include <logdepthbuf_vertex>
  }
`;
const FRAG = /* glsl */`
  #include <common>
  #include <logdepthbuf_pars_fragment>
  uniform vec3 uSunLocal, uCamLocal;
  uniform float uStrength, uViewerDark, uPhase, uDetailPhase, uRadius;
  varying vec3 vLocal;
  varying vec2 vSheet;
  varying float vFacet, vSeed;
  void main() {
    #include <logdepthbuf_fragment>
    // Also hide the far hemisphere from orbit; no reliance on a resident terrain tile.
    vec3 ray=vLocal-uCamLocal; float distance=length(ray); ray/=distance;
    float b=dot(uCamLocal,ray), c=dot(uCamLocal,uCamLocal)-uRadius*uRadius;
    float d=b*b-c;
    if(d>0.0 && -b-sqrt(d)>0.0 && -b-sqrt(d)<distance-1.0) discard;
    float night=1.0-smoothstep(-.20,.03,dot(normalize(vLocal),uSunLocal));
    float a=vSheet.x, h=vSheet.y;
    float broad=.60+.25*sin(a*4.0+vSeed+uPhase)+.15*sin(a*9.0-vSeed-uPhase);
    float rays=.67+.20*sin(a*49.0+vSeed+uDetailPhase)+.13*sin(a*103.0-vSeed-uDetailPhase);
    // A soft lower edge with a bright green fold, then long dim violet veils.
    float profile=smoothstep(0.0,.065,h)*(1.0-smoothstep(.18,1.0,h));
    float upper=smoothstep(.12,.64,h);
    vec3 color=mix(vec3(.12,.62,.39),vec3(.32,.15,.56),upper);
    color=mix(color,vec3(.11,.38,.51),.16+.10*sin(a*5.0+vSeed));
    float emission=profile*broad*rays*(.78+.34*vFacet)*uStrength*night*uViewerDark;
    gl_FragColor=vec4(color,emission*.45);
    #include <colorspace_fragment>
  }
`;

export function makeAurora(def: PlanetDef) {
  const config=auroraConfig(def), state=auroraAt(config,0);
  const axis=new THREE.Vector3(...config.axis), east=new THREE.Vector3().crossVectors(axis,Math.abs(axis.y)<.9?new THREE.Vector3(0,1,0):new THREE.Vector3(1,0,0)).normalize();
  const north=new THREE.Vector3().crossVectors(east,axis).normalize();
  const basis=new THREE.Matrix3().set(east.x,axis.x,north.x,east.y,axis.y,north.y,east.z,axis.z,north.z);
  const positions:number[]=[],curtains:number[]=[],facets:number[]=[],rng=rngFromString(`${def.seed}:aurora:curtains:v1`);
  const rows=[0,.10,.25,.46,.72,1], segments=192;
  for(const hemisphere of [-1,1]) for(let band=0;band<3;band++) {
    const seed=rng()*Math.PI*2;
    for(let x=0;x<segments;x++)for(let y=0;y<rows.length-1;y++) {
      const a=x/segments*Math.PI*2,b=(x+1)/segments*Math.PI*2;
      for(const triangle of [[[a,rows[y]],[b,rows[y]],[a,rows[y+1]]],[[b,rows[y]],[b,rows[y+1]],[a,rows[y+1]]]]) {
        const facet=rng();
        for(const [angle,height] of triangle) { positions.push(angle,height,0);curtains.push(band,hemisphere,seed);facets.push(facet); }
      }
    }
  }
  const geometry=new THREE.BufferGeometry();
  geometry.setAttribute('position',new THREE.Float32BufferAttribute(positions,3));
  geometry.setAttribute('curtain',new THREE.Float32BufferAttribute(curtains,3));
  geometry.setAttribute('facet',new THREE.Float32BufferAttribute(facets,1));
  geometry.boundingSphere=new THREE.Sphere(new THREE.Vector3(),def.radius+(def.atmosphere?.height??0)*7.5);
  const uniforms={uBasis:{value:basis},uRadius:{value:def.radius},uHeight:{value:def.atmosphere?.height??0},
    uOval:{value:state.oval},uPhase:{value:0},uDetailPhase:{value:0},uStrength:{value:0},uViewerDark:{value:0},
    uSunLocal:{value:new THREE.Vector3()},uCamLocal:{value:new THREE.Vector3()}};
  const material=new THREE.ShaderMaterial({uniforms,vertexShader:VERT,fragmentShader:FRAG,
    transparent:true,depthWrite:false,side:THREE.DoubleSide,blending:THREE.AdditiveBlending,toneMapped:false});
  const mesh=new THREE.Mesh(geometry,material);mesh.name='aurora-curtains';mesh.renderOrder=3.5;mesh.visible=false;
  // Double-sided additive emission needs one pass (two would double its energy).
  material.forceSinglePass=true;
  function update(time:number,camLocal:THREE.Vector3,sunLocal:THREE.Vector3) {
    auroraAt(config,time,state);
    const radius=camLocal.length();
    const groundDark=auroraDarkness(camLocal.dot(sunLocal)/Math.max(radius,1));
    // In space the day-side observer's sky is black; individual curtain columns
    // still fade on the sunlit side. No camera-relative rotation of the oval.
    const space=THREE.MathUtils.smoothstep(radius,def.radius+(def.atmosphere?.height??0)*3,def.radius+(def.atmosphere?.height??0)*7);
    uniforms.uViewerDark.value=THREE.MathUtils.lerp(groundDark,1,space);
    uniforms.uStrength.value=state.strength;uniforms.uOval.value=state.oval;
    uniforms.uPhase.value=state.phase;uniforms.uDetailPhase.value=state.detailPhase;
    uniforms.uCamLocal.value.copy(camLocal);uniforms.uSunLocal.value.copy(sunLocal);
    mesh.visible=config.enabled&&state.strength*uniforms.uViewerDark.value>.0001;
  }
  return {mesh,config,state:state as Readonly<AuroraState>,update,
    dispose:()=>{mesh.removeFromParent();geometry.dispose();material.dispose();}};
}
